import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api-client';

type Product = {
  sku: string;
  designation: string;
  famille: string | null;
  dn: number | null;
  pn: number | null;
  materiau: string | null;
  prix_unitaire_ht: number;
  unite: string | null;
};

/**
 * Combobox SKU : input text + dropdown des produits correspondants.
 * Navigation clavier : ↑ ↓ Enter Escape.
 *
 * Story : Sprint 15 (remplace les inputs free-text dans correction ligne)
 */
export function SkuAutocomplete({
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  value: string;
  onChange: (sku: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState(value);
  const [items, setItems] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Debounce search 200ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setItems([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      api
        .get<{ items: Product[] }>(`/api/catalogues/search?q=${encodeURIComponent(query)}&limit=10`)
        .then((d) => {
          setItems(d.items ?? []);
          setActiveIdx(0);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function select(p: Product) {
    setQuery(p.sku);
    onChange(p.sku);
    setOpen(false);
  }

  function handleKey(e: React.KeyboardEvent) {
    if (!open || items.length === 0) {
      if (e.key === 'ArrowDown' && items.length > 0) {
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + items.length) % items.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = items[activeIdx];
      if (p) select(p);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => items.length > 0 && setOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder ?? 'Taper pour chercher…'}
        autoFocus={autoFocus}
        className="w-full border rounded px-2 py-1.5 text-sm font-mono"
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && (items.length > 0 || loading) && (
        <ul
          role="listbox"
          className="absolute z-20 left-0 right-0 top-full mt-1 bg-white dark:bg-gray-800 border rounded shadow-lg max-h-72 overflow-y-auto"
        >
          {loading && items.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-500">Recherche…</li>
          )}
          {items.map((p, i) => (
            <li
              key={p.sku}
              role="option"
              aria-selected={i === activeIdx}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => select(p)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                i === activeIdx ? 'bg-primary-50 dark:bg-gray-700' : ''
              }`}
            >
              <div className="font-mono text-xs">{p.sku}</div>
              <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                {p.designation}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {[
                  p.famille,
                  p.dn && `DN${p.dn}`,
                  p.pn && `PN${p.pn}`,
                  p.materiau,
                ]
                  .filter(Boolean)
                  .join(' · ')}{' '}
                · {p.prix_unitaire_ht.toFixed(2)} € / {p.unite ?? 'U'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
